/**
 * Input widgets (input box, slider, zone slider) and the value they show —
 * used when an input's value is calculated by its Value formula.
 */

import type { DesignNode } from '../design-builder.types';
import { parseNumber } from './design-slide.utils';

/** Widgets that hold a single value someone can change. */
const INPUT_WIDGETS = new Set(['INPUT_BOX', 'SLIDER', 'ZONE_SLIDER']);

export function isInputWidget(node: DesignNode): boolean {
  return node.type === 'WIDGET' && INPUT_WIDGETS.has(node.widget?.layoutType ?? '');
}

/**
 * The widget's `[data]` with `value` pushed in, in the shape its component
 * reads. A slider is only moved by a real number.
 */
export function withInputValue(layoutType: string, data: Record<string, unknown>, value: number | string): Record<string, unknown> {
  if (layoutType === 'INPUT_BOX') return { ...data, value };
  const numeric = typeof value === 'number' ? value : parseNumber(String(value));
  if (numeric === null || !Number.isFinite(numeric)) return data;
  if (layoutType === 'ZONE_SLIDER') return { ...data, value: numeric };
  if (layoutType === 'SLIDER') {
    // `app-mat-slider` reads `data.data.data.{rangeEnd}` (tile shape) or `data.data.{rangeEnd}`.
    const outer = (data['data'] ?? {}) as Record<string, unknown>;
    const inner = outer['data'] as Record<string, unknown> | undefined;
    return inner && typeof inner === 'object'
      ? { ...data, data: { ...outer, data: { ...inner, rangeEnd: numeric } } }
      : { ...data, data: { ...outer, rangeEnd: numeric } };
  }
  return data;
}
